import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import { callStatusOptions, checkModulePermission, fileDownload } from '../../../utils/common';
import FormDateInput from '../../../components/form/FormDateInput';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { callexCallLogsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallexCallDetailsModal from './CallexCallDetailsModal';

const CallexAiCallLogReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({ calls: [], pagination: {} });
  const [audioDetails, setAudioDetails] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], data: [] });
  const timezone = useTimezone();
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);
  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    status: '',
    agentId: '',
    startDate: today,
    endDate: today
  };

  const validationSchema = Yup.object({
    agentId: Yup.string()
  });

  const onSubmit = async (valuesToUse) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = {
        ...valuesToUse,
        startDate: valuesToUse.startDate
          ? format(new Date(valuesToUse.startDate), 'yyyy-MM-dd')
          : undefined,
        endDate: valuesToUse.endDate
          ? format(new Date(valuesToUse.endDate), 'yyyy-MM-dd')
          : undefined
      };

      const response = await dispatch(callexCallLogsGet(payload)).unwrap();
      if (response && response.success) {
        setReportData(response.data);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (v) => onSubmit(v)
    });

  useEffect(() => {
    onSubmit(values);
  }, [values.limit, values.page]);

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data?.agents.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getAIAgentOption();
  }, []);

  const callStatusColors = {
    completed: '#2cd07e33', // Green
    busy: '#ffb22b33', // Yellow
    failed: '#ff456033', // Red
    'no-answer': '#545b6233' // Gray
  };

  const formatDate = (startedAt) => {
    if (!startedAt) return '-';
    const date = startedAt._seconds ? new Date(startedAt._seconds * 1000) : new Date(startedAt);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  function formatTime(value) {
    if (value === undefined || value === null) return '0:00';
    const totalSeconds = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(totalSeconds)) return '0:00';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const headers = [
        { label: 'Call Date', key: 'date' },
        { label: 'Name', key: 'agentName' },
        { label: 'Phone Number', key: 'phoneNumber' },
        { label: 'Duration', key: 'duration' },
        { label: 'Disposition', key: 'disposition' },
        { label: 'Status', key: 'status' },
        { label: 'Sentiment', key: 'sentiment' },
        { label: 'Summary', key: 'summary' },
        { label: 'Outcome', key: 'outcome' }
      ];

      const response = await dispatch(callexCallLogsGet()).unwrap();
      const allCalls = response?.success ? response.data?.calls || [] : [];

      const data = (allCalls || []).map((item) => ({
        date: formatDate(item.startedAt),
        agentName: item.agentName,
        phoneNumber: item.phoneNumber || '-',
        duration: formatTime(item.duration),
        disposition: item.disposition || '-',
        status: item.status,
        sentiment: item.sentiment,
        summary: item.summary || '',
        outcome: item.outcome || ''
      }));

      setCsvData({ headers, data });
      setIsCsvDownload(true);
      setIsExporting(false);
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="AI Call Logs Report"
        isSearch
        isExportDisabled={!checkModulePermission('is_export', 'Call Logs', userRolePermission)}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={(reportData.calls || []).length > 0}
        csvProps={{ ...csvData, filename: 'callex_ai_call_logs.csv' }}
        onExportClick={handleExportCSV}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />

      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="startDate"
                  label="From Date"
                  placeholder="Select From Date"
                  value={values.startDate}
                  onChange={(e) => setFieldValue('startDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  maxDate={values.endDate || today}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="endDate"
                  label="To Date"
                  placeholder="Select To Date"
                  value={values.endDate}
                  onChange={(e) => setFieldValue('endDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  minDate={values.startDate}
                  maxDate={today}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="status"
                  label="Call Status"
                  placeholder="Select Call Status"
                  value={values.status}
                  options={callStatusOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="agentId"
                  label="AI Agent"
                  placeholder="Select AI Agent"
                  value={values.agentId}
                  options={aiPhoneNumberOption}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : (reportData.calls || []).length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Call Date</th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Duration</th>
                  <th>Disposition</th>
                  <th className="text-center">Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Call Logs
                  </th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Recording
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.calls.map((val, index) => (
                  <tr key={val.id || index}>
                    <td>{formatDate(val.startedAt)}</td>
                    <td>{val.agentName}</td>
                    <td>{val.phoneNumber || '-'}</td>
                    <td>{formatTime(val.duration)}</td>
                    <td>{val.disposition || '-'}</td>
                    <td
                      style={{
                        backgroundColor: callStatusColors[val.status?.toLowerCase()] || '#e9ecef',
                        textTransform: 'capitalize',
                        textAlign: 'center'
                      }}>
                      {val.status}
                    </td>
                    <td className="text-center">
                      <Icon.FileText
                        size={16}
                        className="cursor-pointer text-blue"
                        onClick={() => setCallDetails(val)}
                      />
                    </td>
                    <td className="text-center">
                      {val.hasRecording ? (
                        <div className="d-flex justify-content-center gap-4">
                          <Icon.PlayCircle
                            size={16}
                            onClick={() => setAudioDetails({ src: val.recordingUrl })}
                            className="cursor-pointer text-info"
                          />
                          <Icon.Download
                            size={16}
                            className="cursor-pointer"
                            onClick={() => fileDownload(val.recordingUrl)}
                          />
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData.pagination?.total || 0}
              limit={reportData.pagination?.limit || 10}
              page={reportData.pagination?.page || 1}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit(values)}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}

      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
          isToken={false}
          isNotFetch={true}
        />
      )}

      {!!callDetails && (
        <CallexCallDetailsModal
          isOpen={!!callDetails}
          callId={callDetails?.id}
          onClose={() => setCallDetails(null)}
        />
      )}
    </div>
  );
};

export default CallexAiCallLogReport;
